// Shared value formatting used across every page that displays market data.
//
// The single rule this file exists to enforce: a value the API did not supply
// renders as an explicit "unavailable" marker, never as a substitute number.

export const UNAVAILABLE = 'N/A';

// Guard on null/undefined/NaN rather than falsiness. `!value` would treat a
// legitimate reading of exactly 0.00 as missing.
export const isMissing = (value) =>
  value === null || value === undefined || value === '' || Number.isNaN(Number(value));

export const isPresent = (value) => !isMissing(value);

/** A rate as a percentage, or UNAVAILABLE. Never invents a number. */
export const formatRate = (value, digits = 2) =>
  isMissing(value) ? UNAVAILABLE : `${Number(value).toFixed(digits)}%`;

/** A signed change, or UNAVAILABLE. */
export const formatChange = (value, digits = 2) => {
  if (isMissing(value)) return UNAVAILABLE;
  const number = Number(value);
  return `${number >= 0 ? '+' : ''}${number.toFixed(digits)}%`;
};

export const formatCurrency = (value, digits = 2) =>
  isMissing(value) ? UNAVAILABLE : `$${Number(value).toFixed(digits)}`;

export const formatIndex = (value, digits = 1) =>
  isMissing(value) ? UNAVAILABLE : Number(value).toFixed(digits);

/** Difference of two optional values - null unless both are present. */
export const difference = (a, b) => (isMissing(a) || isMissing(b) ? null : Number(a) - Number(b));

/** Sum of two optional values - null unless both are present. */
export const sum = (a, b) => (isMissing(a) || isMissing(b) ? null : Number(a) + Number(b));

export const formatValue = (value, type = 'percent') => {
  if (isMissing(value)) return UNAVAILABLE;

  const number = Number(value);

  switch (type) {
    case 'percent':
      return `${number.toFixed(2)}%`;
    case 'decimal':
      return number.toFixed(3);
    case 'thousands':
      return `${(number / 1000).toFixed(1)}K`;
    case 'millions':
      return `${(number / 1000000).toFixed(2)}M`;
    // Balance-sheet figures run to thousands of billions, and "$4800B" is a
    // number you have to stop and count. Roll up to trillions past 1,000B.
    case 'billions':
      return Math.abs(number) >= 1000
        ? `$${(number / 1000).toFixed(2)}T`
        : `$${number.toFixed(0)}B`;
    case 'billions_month':
      return Math.abs(number) >= 1000
        ? `$${(number / 1000).toFixed(2)}T/mo`
        : `$${number.toFixed(0)}B/mo`;
    case 'index':
      return number.toFixed(1);
    case 'currency':
      return `$${number.toFixed(2)}`;
    default:
      return String(value);
  }
};

export const getChangeIndicator = (current, previous) => {
  if (isMissing(current) || isMissing(previous)) {
    return { icon: '→', color: 'text-gray-500', text: 'No change data' };
  }

  const change = Number(current) - Number(previous);
  if (Math.abs(change) < 0.01) {
    return { icon: '→', color: 'text-gray-500', text: 'Unchanged' };
  }

  // Rising rates are bad news for borrowers, hence red for up.
  return change > 0
    ? { icon: '↗', color: 'text-red-500', text: `+${change.toFixed(2)}` }
    : { icon: '↘', color: 'text-green-500', text: change.toFixed(2) };
};

export const trendColor = (trend) =>
  trend === 'up' ? 'text-red-600' : trend === 'down' ? 'text-green-600' : 'text-gray-500';

export const trendBadge = (trend) =>
  trend === 'up'
    ? 'bg-red-100 text-red-700'
    : trend === 'down'
      ? 'bg-green-100 text-green-700'
      : 'bg-gray-100 text-gray-600';

export const trendArrow = (trend) => (trend === 'up' ? '↗' : trend === 'down' ? '↘' : '→');

export const trendLabel = (trend) =>
  trend === 'up' ? '↗️ Rising' : trend === 'down' ? '↘️ Falling' : '→ Flat';
